// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Accordion from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { AccordionSectionHeaderProps } from "@insite/mobius/AccordionSection/AccordionSectionHeader";
import { IconWrapper } from "@insite/mobius/Icon";
import { TabProps } from "@insite/mobius/Tab/Tab";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { BreakpointKey } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import { XngTabGroupsProps } from "@vanmeter/MobiusOverrides/TabGroup/TabGroup";
import { color, mergeWithCss, xScreen } from "@vanmeter/XDL/Utilities";
import * as React from "react";
import { FC, useState } from "react";
import { css } from "styled-components";

type AssumedChildrenType = React.ReactElement<TabProps>[];

export interface TabGroupMobileProps {
    showOnMobile?: BreakpointKey;
    mobileCssOverrides?: TabGroupMobileStyles;
}

export interface OwnProps {
    currentTab?: string;
    changeTab: (tabKey: string, event: React.MouseEvent | React.KeyboardEvent) => void;
}

type Props = XngTabGroupsProps & OwnProps & TabGroupMobileProps;

interface TabGroupMobileStyles {
    wrapper?: InjectableCss;
    accordionSection?: AccordionSectionPresentationProps;
    accordionSectionCollapsed?: AccordionSectionPresentationProps;
}
const tabSectionStyles: AccordionSectionPresentationProps = {
    titleTypographyProps: {
        variant: "h5",
    },
    toggleIconProps: {
        ...({ width: 14, height: 16 } as any),
    },
    headerProps: {
        css: css`
            &&& button,
            &&& button:focus {
                padding: 8px;
            }

            ${({ expanded }: AccordionSectionHeaderProps) =>
                !expanded
                    ? css`
                          &&& button,
                          &&& button:focus {
                              margin-bottom: 8px;
                              background: ${color("common.ui2")};
                          }
                          ${TypographyStyle} {
                              color: ${color("common.ui7")};
                          }
                          ${IconWrapper} {
                              color: ${color("common.ui5")};
                          }
                      `
                    : css`
                          &&&& button {
                              background-color: ${color("common.ui16")};
                              ${TypographyStyle}, ${IconWrapper} {
                                  color: ${color("common.ui1")};
                              }
                          }
                      `}
        `,
    },
    panelProps: {
        css: css`
            padding: 16px 0 16px;
        `,
    },
};

const tabGroupMobileStyles: TabGroupMobileStyles = {
    wrapper: {
        css: css`
            display: block;
            width: 100%;

            ${({ showOnMobile }: any) => css`
                ${xScreen("gt", showOnMobile)} {
                    display: none;
                }
            `}

            @media print {
                display: none;
            }
        `,
    },
    accordionSection: {
        ...tabSectionStyles,
    },
    accordionSectionCollapsed: {
        ...mergeWithCss(tabSectionStyles, {
            headerProps: {
                css: css`
                    &&& button,
                    &&& button:focus {
                        background: ${color("common.ui2")};
                    }
                `,
            },
        }),
        toggleIconProps: {
            ...tabSectionStyles.toggleIconProps,
            color: "common.ui5",
        },
        titleTypographyProps: {
            ...tabSectionStyles.titleTypographyProps,
            color: "common.ui7",
        },
    },
};

const TabGroupMobile: FC<Props> = ({ showOnMobile = "lg", currentTab, changeTab, mobileCssOverrides, children }) => {
    const [styles] = useState(() => mergeWithCss(tabGroupMobileStyles, mobileCssOverrides));
    const { isTabletScreen } = useAppSelector(state => {
        return {
            isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        };
    });

    React.useEffect(() => {
        const current = tabs.find(tab => tab.key === currentTab);
    }, [currentTab]);

    const tabs: { key: string; label: string; headlineNumber?: string; children?: React.ReactNode }[] = [];
    React.Children.forEach(children as AssumedChildrenType, thisTab => {
        if (!thisTab) {
            return;
        }
        const { tabKey, hidden, children } = thisTab.props;
        if (!hidden) {
            tabs.push({
                key: tabKey,
                label: typeof thisTab.props.headline === "string" ? thisTab.props.headline : thisTab.props.tabKey || "",
                headlineNumber: (thisTab.props as any).headlineNumber,
                children,
            });
        }
    });

    const onTabChangeHandler = (keyTab: string, event: any) => {
        changeTab(currentTab === keyTab ? "" : keyTab, event);
    };

    if (!tabs.length || !isTabletScreen) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} className="TabGroupWrapperMobile" {...{ showOnMobile }}>
            <Accordion headingLevel={4}>
                {tabs.map(({ key, label, headlineNumber, children }, index) => {
                    return (
                        <ManagedAccordionSection
                            key={key}
                            title={label}
                            expanded={currentTab === key}
                            initialExpanded={index === 0}
                            onTogglePanel={e => {
                                onTabChangeHandler(key, e);
                            }}
                            {...(currentTab === key ? styles.accordionSection : styles.accordionSectionCollapsed)}
                            {...{ headlineNumber }}
                        >
                            {children}
                        </ManagedAccordionSection>
                    );
                })}
            </Accordion>
        </StyledWrapper>
    );
};

/** @component */
export default TabGroupMobile;
